import authService from './authService';
import { getAuth } from 'firebase/auth';

class ChatService {
  constructor() {
    if (ChatService.instance) {
      return ChatService.instance;
    }
    ChatService.instance = this;
  }

  async sendMessageWithoutCheck(message) {
    let response;
    try {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      const userId = auth.currentUser?.uid;

      if (!token || !userId) {
        throw new Error('Authentication token or user ID is missing');
      }

      response = await fetch(`${process.env.REACT_APP_API_URL}/chat/bypass`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message })
      });

      return this.processResponse(response);
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  async *streamResponse(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        
        // Process all complete chunks
        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(5));
              if (data.type === 'message' && data.content) {
                // Handle nested SSE format in content
                const contentLines = data.content.split('\r\n');
                for (const contentLine of contentLines) {
                  if (contentLine.startsWith('data: ')) {
                    try {
                      const messageData = JSON.parse(contentLine.slice(5));
                      if (messageData.messages && messageData.messages.length > 0) {
                        yield messageData;
                      } else if (messageData.agents && messageData.agents.length > 0) {
                        for (const agent of messageData.agents) {
                          if (agent.answer) {
                            yield {
                              messages: [{
                                content: agent.answer,
                                type: 'assistant'
                              }]
                            };
                          }
                        }
                      }
                    } catch (e) {
                      console.error('Error parsing nested SSE data:', e);
                    }
                  }
                }
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
        
        // Keep the last incomplete chunk
        buffer = lines[lines.length - 1];
      }
    } finally {
      reader.releaseLock();
    }
  }

  async processResponse(response) {
    try {
      if (response.status === 409) {
        const error = await response.json();
        throw new Error(error.error || 'Thread is busy. Please wait a moment and try again.');
      }

      const text = await response.text();
      console.log('Raw response text:', text);

      let jsonResponse;
      try {
        jsonResponse = JSON.parse(text);
      } catch (e) {
        console.error('Initial JSON parse failed:', e);
        // Try to clean the text before parsing
        const cleanText = text
          .trim()
          .replace(/^\s*data:\s*/, '')  // Remove data: prefix
          .replace(/\n/g, '\\n')
          .replace(/\r/g, '\\r')
          .replace(/\t/g, '\\t');
        
        try {
          jsonResponse = JSON.parse(cleanText);
        } catch (e2) {
          console.error('Failed to parse cleaned text:', e2);
          throw new Error('Invalid response format from server');
        }
      }

      console.log('Parsed response:', jsonResponse);
      
      // Extract the content from the response
      const content = jsonResponse.messages?.[jsonResponse.messages.length - 1]?.content || 
                     jsonResponse.text || 
                     '';

      return {
        content,
        usage: jsonResponse.usage || {},
        metadata: jsonResponse.metadata || {}
      };
    } catch (error) {
      console.error('Error processing response:', error);
      throw error;
    }
  }

  async getConversationHistory(limit, before) {
    try {
      const token = await authService.getAuthToken();
      const userId = await authService.getUserId();

      if (!token || !userId) {
        throw new Error('Authentication token or user ID is missing');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/chat/getConversationHistory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId, limit, before })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Network response was not ok');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching conversation history:', error);
      throw new Error(`Chat service error: ${error.message}`);
    }
  }

  async clearConversationHistory() {
    try {
      const token = await authService.getAuthToken();
      const userId = await authService.getUserId();

      if (!token || !userId) {
        throw new Error('Authentication token or user ID is missing');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/chat/clearConversationHistory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Network response was not ok');
      }

      return await response.json();
    } catch (error) {
      console.error('Error clearing conversation history:', error);
      throw new Error(`Chat service error: ${error.message}`);
    }
  }

  setMessageUpdateCallback(callback) {
    this.onMessageUpdate = callback;
  }
}

// Create and export a single instance
const chatService = new ChatService();
export default chatService;
